import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { AnalyticsRangeContext, ComparePeriod } from '@services/analytics-services/core';
import { DashboardStore } from '../dashboard/dashboard.store';
import { Subject, takeUntil } from 'rxjs';
import { DashboardInsightsStore } from './dashboard-insights.store';
import { CustomBarChartContainerComponent } from '@enginuity/analytics/organisms/custom-bar-chart-container/custom-bar-chart-container.component';
import { HighChartContainerComponent } from '@enginuity/analytics/organisms/high-chart-container/high-chart-container.component';
import { TrafficMatrixComponent } from '@enginuity/analytics/molecules/traffic-matrix/traffic-matrix.component';
import { AnalyticsTrendWidgetComponent } from '@enginuity/analytics/organisms/analytics-trend-widget/analytics-trend-widget.component';
import { AnalyticsWidgetComponent } from '@enginuity/analytics/organisms/analytics-widget/analytics-widget.component';
import {} from '@services/analytics-services/core/group-chart.modals';
import { ProcessingState, Pagination } from '@services/core-services/models';

@Component({
  selector: 'app-dashboard-insights',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    AnalyticsTrendWidgetComponent,
    TrafficMatrixComponent,
    AnalyticsWidgetComponent,
    CustomBarChartContainerComponent,
    HighChartContainerComponent,
  ],
  templateUrl: './dashboard-insights.component.html',
  styleUrl: './dashboard-insights.component.scss',
  providers: [DashboardInsightsStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardInsightsComponent implements OnInit, OnDestroy {
  protected context!: AnalyticsRangeContext;
  protected comparePeriod!: ComparePeriod;
  private destroy$ = new Subject<void>();

  visitorMetricTitles: string[] = ['Visitors'];
  protected activeUsers$ = this.dashboardInsightsStore.getActiveUsers();
  protected activeUsersLoader$ = this.dashboardInsightsStore.getActiveUsersLoader();
  protected visitorAnalytics$ = this.dashboardInsightsStore.getVisitorAnalytics();
  protected viewAnalytics$ = this.dashboardInsightsStore.getViewAnalytics();
  protected eventAnalytics$ = this.dashboardInsightsStore.getEventAnalytics();
  protected trafficMatrixData$ = this.dashboardInsightsStore.getTrafficMatrixData();
  protected trafficMatrixLoader$ = this.dashboardInsightsStore.getTrafficMatrixLoader();
  protected pageStatsChartOptions$ = this.dashboardInsightsStore.getPageStatsChartOptions();
  protected pageStatsLoader$ = this.dashboardInsightsStore.getPageStatsLoader();
  protected visitorsByDeviceChartOptions$ =
    this.dashboardInsightsStore.getVisitorsByDeviceChartOptions();
  protected visitorsByDeviceLoader$ = this.dashboardInsightsStore.getVisitorsByDeviceLoader();
  protected serverEventsChartOptions$ = this.dashboardInsightsStore.getServerEventsChartOptions();
  protected serverEventsLoader$ = this.dashboardInsightsStore.getServerEventsLoader();
  protected visitorsBySourceStats$ = this.dashboardInsightsStore.getVisitorsBySourceStats();
  protected visitorsBySourceTabs$ = this.dashboardInsightsStore.getVisitorsBySourceTabs();
  protected visitorsBySourceActiveTab$ = this.dashboardInsightsStore.getVisitorsBySourceActiveTab();
  protected visitorsBySourcePagination$ =
    this.dashboardInsightsStore.getVisitorsBySourcePagination();
  protected visitorsBySourceLoader$ = this.dashboardInsightsStore.getVisitorsBySourceLoader();
  protected pageStatsError$ = this.dashboardInsightsStore.getPageStatsError();
  protected visitorsBySourceError$ = this.dashboardInsightsStore.getVisitorBySourceError();
  protected visitorsByDeviceError$ = this.dashboardInsightsStore.getVisitorsByDeviceError();
  protected serverEventsError$ = this.dashboardInsightsStore.getServerEventsError();
  protected eventAnalyticsErrorMessage$ =
    this.dashboardInsightsStore.getEventAnalyticsErrorMessage();
  protected visitorAnalyticsErrorMessage$ =
    this.dashboardInsightsStore.getVisitorAnalyticsErrorMessage();
  protected viewAnalyticsErrorMessage$ = this.dashboardInsightsStore.getViewAnalyticsErrorMessage();
  protected generalErrorMessage$ = this.dashboardInsightsStore.getGeneralErrorMessage();
  protected readonly ProcessingState = ProcessingState;

  protected defaultPagination: Pagination = {
    // limit: 8, // Removed based on previous error
    total_records: 0,
    total_pages: 1,
    next_page: null,
    previous_page: null,
  };

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dashboardInsightsStore: DashboardInsightsStore
  ) {}

  ngOnInit() {
    this.dashboardStore
      .getComparePeriod()
      .pipe(takeUntil(this.destroy$))
      .subscribe(period => (this.comparePeriod = period));

    this.dashboardStore
      .getRangeContext()
      .pipe(takeUntil(this.destroy$))
      .subscribe(context => {
        this.context = context;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onVisitorsTabChange(tabId: string): void {
    this.dashboardInsightsStore.updateVisitorsBySourceDefaultTab(tabId);
  }

  protected onVisitorsPageChange(event: { tabId: string; page: number; limit: number }): void {
    this.dashboardInsightsStore.updateVisitorsBySourcePagination(
      event.tabId,
      event.page,
      event.limit
    );
  }
}
